/**
 * UI review Medium: Settings → Engagement, AI & translation, and Fleet. Each panel edits one section of the workspace
 * prefs and saves as you go (t647u: text after a pause, switches and choices straight away, with Saving… / Saved or the
 * reason it wasn't saved); the API checks the section's permission and enforces every value.
 */
import { DeliveryChannelSelect, useDeliveryChannels } from '@/features/fleet/DeliveryChannelSelect';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CHAT_LIMIT_BOUNDS, DEFAULT_TRACKING_CONSENT, emailDelayOptions, type WorkspacePrefs, type WorkspacePrefSection } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { Card, ErrorBox, Field, Input, Select, Spinner, Textarea, Toggle } from '@/components/ui';
import { SaveStatus, useAutosaveForm } from '@/components/ui/autosave';
import { EditableHint } from '@/components/ui/EditableHint';

export const prefsKey = (ws: string) => ['workspace-prefs', ws] as const;
export function useWorkspacePrefs() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: prefsKey(ws ?? ''), queryFn: () => api.workspacePrefs(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Effective chat limits (channel, group message, call; calls already capped by the server ceiling). */
export function useChatLimits() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['chat-limits', ws], queryFn: () => api.chatLimits(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000 }).data;
}

function useSection<K extends WorkspacePrefSection>(section: K) {
  const ws = useWorkspaceId();
  const q = useWorkspacePrefs();
  const form = useAutosaveForm<WorkspacePrefs[K]>(q.data?.[section], async v => {
    if (!ws) return;
    queryClient.setQueryData(prefsKey(ws), await api.saveWorkspacePrefs(ws, section, v));
  }, { fallback: {} as WorkspacePrefs[K] });
  const draft = q.data ? form.draft : null;
  /** `now`: switches and choices save straight away; typing waits for a pause. */
  const setDraft = (v: WorkspacePrefs[K], now = false) => form.set(v, now);
  const bar = <div className="flex justify-end"><SaveStatus s={form.status} testId={`prefs-${section}-status`} /></div>;
  return { q, draft, setDraft, bar, flush: form.flush };
}

export function EngagementPanel() {
  const { q, draft, setDraft, bar } = useSection('engagement');
  const ws = useWorkspaceId();
  const channels = useQuery({ queryKey: ['channels-browse', ws], queryFn: () => api.browseChannels(ws!), enabled: Boolean(ws) });
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Spinner />;
  const publicChannels = (channels.data?.channels ?? []).filter(c => !c.is_private && !c.archived_at && c.slug);
  return (
    <Card title="Celebrations">
      <div className="space-y-4">
        <Toggle checked={draft.birthday_posts} onChange={v => setDraft({ ...draft, birthday_posts: v }, true)} label="Post birthdays in the kudos feed and a channel" />
        <Field label="Birthday channel" hint="People who hide their birthday are never announced.">
          <Select value={draft.birthday_channel_slug} disabled={!draft.birthday_posts} onChange={e => setDraft({ ...draft, birthday_channel_slug: e.target.value }, true)}>
            {!publicChannels.some(c => c.slug === draft.birthday_channel_slug) && <option value={draft.birthday_channel_slug}>#{draft.birthday_channel_slug}</option>}
            {publicChannels.map(c => <option key={c.id} value={c.slug!}>#{c.slug}</option>)}
          </Select>
        </Field>
        <Toggle checked={draft.sales_target_posts} onChange={v => setDraft({ ...draft, sales_target_posts: v }, true)} label="Post a kudos when a building hits its sales target" />
        <p className="text-xs text-slate-500">Hits and bonuses are still recorded when posts are off. Who can give kudos is set per role in <Link className="underline" to="/settings/people/roles">Roles &amp; permissions</Link> (Kudos → Post). Badges are under <Link className="underline" to="/settings/engagement/kudos">Kudos badges</Link>.</p>
        {bar}
      </div>
    </Card>
  );
}

const AI_FEATURES: { key: keyof WorkspacePrefs['ai']; label: string; hint: string }[] = [
  { key: 'translation', label: 'Translation', hint: 'Translate messages, announcements and articles' },
  { key: 'assistant', label: 'Assistant', hint: 'Ask questions about policies, rotas and the knowledge base' },
  { key: 'catch_up', label: 'Catch-up summaries', hint: 'Summarise what you missed in a channel or thread' },
  { key: 'digests', label: 'Weekly digests', hint: 'Scheduled summaries for buildings and teams' },
  { key: 'meeting_notes', label: 'Meeting notes', hint: 'Record calls (with everyone’s consent) and write up notes' },
];

export function AiPanel() {
  const { q, draft, setDraft, bar } = useSection('ai');
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Spinner />;
  return (
    <Card title="AI & translation">
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">Switch features off for everyone. Who may use each one is still set per role, and providers are shown under <Link className="underline" to="/settings/integrations/status">Integrations → Status</Link>.</p>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {AI_FEATURES.map(f => (
            <li key={f.key} className="flex items-center justify-between gap-4 py-3">
              <div><div className="font-medium">{f.label}</div><div className="text-xs text-slate-500">{f.hint}</div></div>
              <Toggle checked={draft[f.key]} onChange={v => setDraft({ ...draft, [f.key]: v }, true)} label={<span className="sr-only">{f.label}</span>} />
            </li>
          ))}
        </ul>
        {bar}
      </div>
    </Card>
  );
}

export function FleetPrefsPanel() {
  const { q, draft, setDraft, bar } = useSection('fleet');
  const channels = useDeliveryChannels();
  const [days, setDays] = useState('');
  useEffect(() => { if (draft) setDays(draft.reminder_days.join(', ')); }, [draft]);
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Spinner />;
  const parse = (v: string) => v.split(/[ ,]+/).filter(Boolean).map(Number);
  return (
    <Card title="Fleet settings">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Compliance reminders (days before)" hint="MOT, tax, insurance, service, tacho, licences and CPC. Up to 4, e.g. 30, 7">
          <Input value={days} inputMode="numeric" onChange={e => setDays(e.target.value)} onBlur={() => { const n = parse(days); if (n.length && n.every(Number.isInteger)) setDraft({ ...draft, reminder_days: n }, true); }} />
        </Field>
        <Field label="Run links last" hint="Hours after the planned end that a self-employed driver’s link keeps working">
          <span className="flex items-center gap-2"><Input type="number" min={1} max={168} className="w-28" value={draft.magic_link_hours} onChange={e => setDraft({ ...draft, magic_link_hours: Number(e.target.value) })} />hours</span>
        </Field>
        <Field label="Delivery updates channel" note="Journey updates go here unless the building has its own channel.">
          <DeliveryChannelSelect value={draft.delivery_channel_id ?? null} missing={channels.data?.workspace.missing} blankLabel="No updates" testId="fleet-delivery-channel"
            onChange={id => setDraft({ ...draft, delivery_channel_id: id }, true)} />
        </Field>
        <Field label="ETA update threshold" hint="A linked thread gets a new ETA when it moves by at least this much">
          <span className="flex items-center gap-2"><Input type="number" min={5} max={240} className="w-28" value={draft.eta_update_threshold_min ?? 15} data-testid="fleet-eta-threshold"
            onChange={e => setDraft({ ...draft, eta_update_threshold_min: Number(e.target.value) })} />minutes</span>
        </Field>
        <Field label="Location sharing consent" className="md:col-span-2" hint="Drivers see this next to the switch before starting a run">
          <Textarea rows={2} maxLength={300} value={draft.tracking_consent_text} placeholder={DEFAULT_TRACKING_CONSENT} onChange={e => setDraft({ ...draft, tracking_consent_text: e.target.value })} />
        </Field>
        {/* t541u: live map, "Stopped for a while" and how long locations are kept. */}
        <Field label="Stopped for a while after" hint="Shown on the live map and journey list when a vehicle has stayed in one place, away from its stops and the depot. 5 to 240">
          <span className="flex items-center gap-2"><Input type="number" min={5} max={240} className="w-28" value={draft.stopped_alert_minutes} onChange={e => setDraft({ ...draft, stopped_alert_minutes: Number(e.target.value) })} data-testid="fleet-stopped-minutes" />minutes</span>
        </Field>
        <Field label="Keep journey locations for" hint="Older locations are deleted each night. 1 to 730">
          <span className="flex items-center gap-2"><Input type="number" min={1} max={730} className="w-28" value={draft.position_retention_days} onChange={e => setDraft({ ...draft, position_retention_days: Number(e.target.value) })} data-testid="fleet-retention-days" />days</span>
        </Field>
        <div className="md:col-span-2">
          <Toggle checked={draft.stopped_notify_manager} onChange={v => setDraft({ ...draft, stopped_notify_manager: v }, true)} label="Notify the journey’s planner when a vehicle has stopped for a while" />
          <EditableHint as="p" id="fleet.settings.stopped-alerts" className="mt-1 text-xs text-slate-500" default="One notification per stop. Off by default; it’s often a break, traffic or a delivery detail." />
        </div>
        <p className="text-xs text-slate-500 md:col-span-2">Home buildings are set per vehicle under <Link className="underline" to="/settings/fleet/vehicles">Vehicles &amp; documents</Link>. The walkaround checklist uses the standard daily-check items.</p>
        <div className="md:col-span-2">{bar}</div>
      </div>
    </Card>
  );
}

const CHAT_LIMIT_HINTS: Record<keyof WorkspacePrefs['chat'], string> = {
  channel_member_limit: 'Most people one channel can hold. People already in a bigger channel stay; nobody new can join until it drops below the limit.',
  room_member_limit: 'Most people one ad-hoc meeting room can hold, including the person who creates it. Ad-hoc meeting rooms are for a call with people from several teams.',
  group_dm_limit: 'Most people in one group message, including the person who starts it. For more, use a channel.',
  call_participant_limit: 'Most people in one voice or video call. Others can still see the call and join when someone leaves.',
};

export function ChatLimitsPanel() {
  const { q, draft, setDraft, bar } = useSection('chat');
  const limits = useChatLimits();
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Spinner />;
  const ceiling = limits?.call_ceiling;
  return (
    <Card title="Chat & call limits">
      <div className="space-y-4" data-testid="chat-limits-panel">
        <EditableHint as="p" id="workspace.chat-limits.intro" className="text-sm text-slate-600 dark:text-slate-300" default="Keep channels, ad-hoc meeting rooms, group messages and calls to a size that works for everyone. The limits apply to the whole workspace." />
        {(Object.keys(CHAT_LIMIT_BOUNDS) as (keyof WorkspacePrefs['chat'])[]).map(k => {
          const b = CHAT_LIMIT_BOUNDS[k];
          const max = k === 'call_participant_limit' && ceiling ? Math.min(b.max, ceiling) : b.max;
          return (
            <Field key={k} label={b.label} hint={<>{CHAT_LIMIT_HINTS[k]} {b.min} to {max}.{k === 'call_participant_limit' && ceiling ? ` This server’s call service handles up to ${ceiling} people in one call.` : ''}</>}>
              <Input type="number" min={b.min} max={max} className="w-28" value={draft[k]} data-testid={`limit-${k}`}
                onChange={e => setDraft({ ...draft, [k]: Number(e.target.value) })} />
            </Field>
          );
        })}
        {bar}
      </div>
    </Card>
  );
}

/** t566u: the organisation's wait before an unseen notification is emailed (people can choose their own). */
export function NotificationEmailsPanel() {
  const { q, draft, setDraft, bar } = useSection('notifications');
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  if (!draft) return <Spinner />;
  return (
    <Card title="Notification emails">
      <div className="space-y-4" data-testid="notification-emails-panel">
        <EditableHint as="p" id="workspace.notification-emails.intro" className="text-sm text-slate-600 dark:text-slate-300" default="When someone hasn’t seen a notification in The Hub, it’s emailed to them after a wait. Seeing it first, on any device, means no email." />
        <Field label="Organisation default" hint="Applies to everyone who hasn’t chosen their own wait in Settings > My account > Notifications.">
          <Select value={String(draft.email_delay_minutes)} data-testid="org-email-delay" onChange={e => setDraft({ ...draft, email_delay_minutes: Number(e.target.value) }, true)}>
            {emailDelayOptions().map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
          </Select>
        </Field>
        {bar}
      </div>
    </Card>
  );
}
